import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, ListChecks, Wallet, Trash2, Save, FileStack, LayoutGrid, ClipboardCopy, Sparkles, Users, Tag, Pencil, Copy, History, FolderOpen, Activity, GitCompareArrows, Search } from "lucide-react";
import ManageCategoriesDialog from "@/pages/app/ManageCategoriesDialog";
import TemplatePropagationDialog from "@/pages/app/TemplatePropagationDialog";
import EditTemplateDialog from "@/pages/app/EditTemplateDialog";
import TaskPeekDialog from "@/components/tasks/TaskPeekDialog";
import ProjectUpdatesPeekDialog from "@/components/portfolio/ProjectUpdatesPeekDialog";
import { toast } from "sonner";
import { TypeIcon } from "@/lib/formatIcon";
import { apiErrorMessage } from "@/lib/apiError";
import { RowsEditor } from "@/components/Brand/editors";
import RateCardsManager from "@/components/packages/RateCardsManager";
import BaselinesAndProfitability from "@/components/packages/BaselinesAndProfitability";
import PackagesDashboardTab from "@/components/packages/PackagesDashboardTab";
import PackagesPlannerIntel from "@/components/packages/PackagesPlannerIntel";
import PackagesRenewalRisk from "@/components/packages/PackagesRenewalRisk";
import ActivityMasterGrid from "@/components/packages/ActivityMasterGrid";
import PackageModeToggle from "@/components/packages/PackageModeToggle";
import BrandAllocationChart from "@/components/packages/BrandAllocationChart";
import PackageHoverPreview from "@/components/packages/PackageHoverPreview";
import PackagesCompareTab from "@/components/packages/PackagesCompareTab";
import usePackageMode from "@/lib/usePackageMode";

export const PKG_STATUS_TONES = {
  draft: "border-border text-muted-foreground", active: "border-emerald-500/40 text-emerald-700",
  paused: "border-amber-500/40 text-amber-700", completed: "border-sky-500/40 text-sky-700",
  cancelled: "border-red-500/40 text-red-700", expired: "border-red-500/40 text-red-700",
};
export const CATEGORIES = ["social", "seo", "ads", "content", "web", "email", "meeting", "documentation", "adhoc", "other"];
export const FREQ_LABELS = { daily: "Daily", weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", half_yearly: "6-monthly", yearly: "Yearly", one_time: "One-time" };
export const fmtMoney = (v, cur = "INR") => `${cur === "INR" ? "₹" : cur + " "}${Number(v || 0).toLocaleString("en-IN")}`;

export default function Packages() {
  const { activeWorkspaceId } = useAuth();
  const modeCtl = usePackageMode();
  // Feb 2026 · Per-subtab permissions. Each subtab is now individually gated;
  // `package.manage` and workspace admins bypass all of them (see
  // `usePackageMode.js` + backend migration v11).
  const {
    canDashboard, canTemplates, canPlanner, canMaster,
    canRates, canIntel, canAllocation,
  } = modeCtl;
  // Feb 2026 · Header "+ Create → Package" shortcut lands here with
  // `?new=1`. Force the Packages sub-tab so the PackagesTab component
  // mounts and the auto-open effect can fire the New Package dialog.
  const [pkgSearchParams] = useSearchParams();
  const forceCreate = pkgSearchParams.get("new") === "1";
  const defaultTab = (
    forceCreate ? "packages" :
    canDashboard ? "dashboard" :
    "packages"
  );
  return (
    <>
      <PageHeader
        eyebrow="Delivery"
        title="Packages"
        description="Monthly deliverable packages per brand — activity master, rate card, pricing & margins."
        testid="packages-header"
        actions={
          <PackageModeToggle
            mode={modeCtl.mode}
            setMode={modeCtl.setMode}
            canToggle={modeCtl.canToggle}
            testid="pkg-list-mode-toggle"
          />
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="rounded-none">
            {canDashboard && (
              <TabsTrigger value="dashboard" className="rounded-none" data-testid="pkg-tab-dashboard"><LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Dashboard</TabsTrigger>
            )}
            <TabsTrigger value="packages" className="rounded-none" data-testid="pkg-tab-packages"><Package className="w-3.5 h-3.5 mr-1.5" /> Packages</TabsTrigger>
            <TabsTrigger value="compare" className="rounded-none" data-testid="pkg-tab-compare"><GitCompareArrows className="w-3.5 h-3.5 mr-1.5" /> Compare</TabsTrigger>
            {canTemplates && (
              <TabsTrigger value="templates" className="rounded-none" data-testid="pkg-tab-templates"><FileStack className="w-3.5 h-3.5 mr-1.5" /> Templates</TabsTrigger>
            )}
            {canPlanner && (
              <TabsTrigger value="planner" className="rounded-none" data-testid="pkg-tab-planner"><LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Planner</TabsTrigger>
            )}
            {canMaster && (
              <TabsTrigger value="master" className="rounded-none" data-testid="pkg-tab-master"><ListChecks className="w-3.5 h-3.5 mr-1.5" /> Activity master</TabsTrigger>
            )}
            {canRates && (
              <TabsTrigger value="rates" className="rounded-none" data-testid="pkg-tab-rates"><Wallet className="w-3.5 h-3.5 mr-1.5" /> Rate card</TabsTrigger>
            )}
            {canIntel && (
              <TabsTrigger value="intel" className="rounded-none" data-testid="pkg-tab-intel"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Baselines & profitability</TabsTrigger>
            )}
            {canAllocation && (
              <TabsTrigger value="allocation" className="rounded-none" data-testid="pkg-tab-allocation"><Users className="w-3.5 h-3.5 mr-1.5" /> Brand allocation</TabsTrigger>
            )}
          </TabsList>
          {canDashboard && (
            <TabsContent value="dashboard" className="pt-6">
              <div className="space-y-6">
                <PackagesDashboardTab wsId={activeWorkspaceId} />
                <PackagesRenewalRisk wsId={activeWorkspaceId} days={30} />
              </div>
            </TabsContent>
          )}
          <TabsContent value="packages" className="pt-6"><PackagesTab wsId={activeWorkspaceId} mode={modeCtl.mode} /></TabsContent>
          <TabsContent value="compare" className="pt-6"><PackagesCompareTab wsId={activeWorkspaceId} /></TabsContent>
          {canTemplates && <TabsContent value="templates" className="pt-6"><TemplatesTab wsId={activeWorkspaceId} /></TabsContent>}
          {canPlanner && <TabsContent value="planner" className="pt-6"><PackagesPlannerIntel wsId={activeWorkspaceId} /></TabsContent>}
          {canMaster && <TabsContent value="master" className="pt-6"><ActivityMasterTab wsId={activeWorkspaceId} /></TabsContent>}
          {canRates && <TabsContent value="rates" className="pt-6"><RateCardTab wsId={activeWorkspaceId} /></TabsContent>}
          {canIntel && <TabsContent value="intel" className="pt-6"><BaselinesAndProfitability wsId={activeWorkspaceId} /></TabsContent>}
          {canAllocation && <TabsContent value="allocation" className="pt-6"><BrandAllocationChart wsId={activeWorkspaceId} /></TabsContent>}
        </Tabs>
      </div>
    </>
  );
}

// ─────────────── Packages list ───────────────

function PackagesTab({ wsId, mode = "financial" }) {
  const [rows, setRows] = useState(null);
  const [brands, setBrands] = useState([]);
  const [status, setStatus] = useState("_all");
  const [brandF, setBrandF] = useState("_all");
  // R268bbbb · Feb 2026 — Quick text search across name / client /
  // brand / line titles. Filters the already-fetched `rows` client-side
  // so it feels instant on every keystroke.
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  // R160e · Feb 2026 — quick-view state for the Project column action
  // buttons. Only one dialog is shown at a time.
  const [tasksProject, setTasksProject] = useState(null);
  const [statusProject, setStatusProject] = useState(null);

  // Feb 2026 · Auto-open the New Package dialog when the header
  // "+ Create" shortcut passes `?new=1`. Consume + strip the flag.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreating(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = () => {
    const p = new URLSearchParams();
    if (status !== "_all") p.set("status", status);
    if (brandF !== "_all") p.set("brand_id", brandF);
    api.get(`/workspaces/${wsId}/packages?${p.toString()}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId, status, brandF]);
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/brands`).then((r) => setBrands(r.data || [])).catch(() => {}); }, [wsId]);

  // Iter · Feb 2026 · R256 — Per-package health chip. Fetch this month's
  // package-vs-calendar diff once and expose a lookup so each row can
  // render on-track / gap without opening the calendar.
  const [pkgHealth, setPkgHealth] = useState({}); // pkg_id → { expected, existing, missing }
  const [healthMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    api.get(`/workspaces/${wsId}/content-calendar/all-packages-diff?month=${healthMonth}`)
      .then((r) => {
        if (cancelled) return;
        const map = {};
        for (const row of (r.data?.packages || [])) {
          map[row.package_id] = { expected: row.expected, existing: row.existing, missing: row.missing };
        }
        setPkgHealth(map);
      })
      .catch(() => { if (!cancelled) setPkgHealth({}); });
    return () => { cancelled = true; };
  }, [wsId, healthMonth, rows]);

  return (
    <div className="space-y-3" data-testid="packages-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {/* R268bbbb · Feb 2026 — Quick text search. Matches package
            name, brand name, client name, and each line's `title` or
            `content_type`. Case-insensitive substring match. */}
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search packages…"
            className="h-9 pl-7 pr-2 w-full text-sm border border-border bg-background rounded-none focus:outline-none focus:border-primary"
            data-testid="pkg-list-search"
          />
        </div>
        <Select value={brandF} onValueChange={setBrandF}>
          <SelectTrigger className="h-9 w-48 rounded-none" data-testid="pkg-brand-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All brands</SelectItem>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="pkg-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All statuses</SelectItem>{Object.keys(PKG_STATUS_TONES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="rounded-none ml-auto" onClick={() => setCreating(true)} data-testid="pkg-new-btn"><Plus className="w-4 h-4 mr-2" /> New package</Button>
      </div>
      {!rows ? <div className="text-sm text-muted-foreground">Loading…</div>
        : (() => {
          // R268bbbb · Feb 2026 — Apply the quick-search filter here so
          // the empty-state below reflects the filtered view.
          const needle = q.trim().toLowerCase();
          const filtered = !needle ? rows : rows.filter((r) => {
            if ((r.name || "").toLowerCase().includes(needle)) return true;
            if ((r.brand_name || "").toLowerCase().includes(needle)) return true;
            if ((r.client_name || "").toLowerCase().includes(needle)) return true;
            const lines = r.lines || [];
            for (const ln of lines) {
              if ((ln.title || "").toLowerCase().includes(needle)) return true;
              if ((ln.content_type || "").toLowerCase().includes(needle)) return true;
            }
            return false;
          });
          if (filtered.length === 0) {
            return needle
              ? <EmptyState title="No packages match your search" description={`Nothing found for "${q}". Try a different term or clear the filter.`} icon={Search} testid="packages-search-empty" />
              : <EmptyState title="No packages yet" description="Create a monthly package for a brand — deliverables, effort, rates and margin in one place." icon={Package} testid="packages-empty" action={<Button className="rounded-none" onClick={() => setCreating(true)} data-testid="packages-empty-cta">New package</Button>} />;
          }
          const rowsForRender = filtered;
          // R77 · Only show money columns when the user has selected Financial
          // mode AND the backend surfaced money fields (i.e. redaction hasn't
          // stripped them). Delivery mode collapses to Hours / Lines.
          const hasPriceField = rowsForRender.some((r) => r.totals && "net_monthly_price" in (r.totals || {}));
          const hasMarginField = rowsForRender.some((r) => r.totals && "margin_pct" in (r.totals || {}));
          const financialLensOn = mode === "financial";
          const canPrice = financialLensOn && hasPriceField;
          const canMargin = financialLensOn && hasMarginField;
          return (
          <div className="border border-border divide-y divide-border" data-testid="packages-list">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <div className="col-span-3">Package</div>
              <div className="col-span-2">Brand · Rate card</div>
              <div className="col-span-2">Project</div>
              <div className="col-span-2">Period</div>
              <div className="col-span-1">Status</div>
              {canPrice ? <div className="col-span-1 text-right">Monthly</div> : <div className="col-span-1 text-right">Hrs/mo</div>}
              {canMargin ? <div className="col-span-1 text-right">Margin</div> : <div className="col-span-1 text-right">Lines</div>}
            </div>
            {rowsForRender.map((p) => (
              <Link key={p.id} to={`/app/packages/${p.id}`} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm hover:bg-muted/30" data-testid={`pkg-row-${p.id}`}>
                <div className="col-span-3 font-semibold truncate">
                  <PackageHoverPreview pid={p.id}>
                    <span className="hover:underline decoration-dotted underline-offset-2 cursor-help"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`pkg-row-name-${p.id}`}>
                      {p.name}
                    </span>
                  </PackageHoverPreview>
                </div>
                <div className="col-span-2 truncate text-muted-foreground">
                  <div className="truncate">{p.brand_name || "—"}</div>
                  {p.rate_card_ref && (
                    <span className="inline-flex items-center gap-1 text-[10px]">
                      <Wallet className="w-2.5 h-2.5" />
                      <span className={`truncate ${p.rate_card_ref.kind === "brand_override" ? "text-violet-600" : ""}`}>{p.rate_card_ref.name}</span>
                    </span>
                  )}
                </div>
                <div className="col-span-2 truncate" data-testid={`pkg-row-project-${p.id}`}>
                  {p.project_id ? (
                    <div className="flex items-center gap-1 min-w-0">
                      <Link
                        to={`/app/projects/${p.project_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="truncate text-primary hover:underline text-xs"
                        title={p.project_name || "Open project"}
                        data-testid={`pkg-row-project-link-${p.id}`}
                      >
                        {p.project_name || "Project"}
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setTasksProject({ id: p.project_id, name: p.project_name || "Project" });
                        }}
                        className="p-1 text-muted-foreground hover:text-primary shrink-0"
                        title="View project tasks"
                        data-testid={`pkg-row-project-tasks-${p.id}`}
                      >
                        <ListChecks className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setStatusProject({ id: p.project_id, name: p.project_name || "Project" });
                        }}
                        className="p-1 text-muted-foreground hover:text-primary shrink-0"
                        title="Project status updates"
                        data-testid={`pkg-row-project-status-${p.id}`}
                      >
                        <Activity className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-muted-foreground italic">Not linked</span>
                  )}
                </div>
                <div className="col-span-2 text-xs font-mono">{p.start_date} → {p.end_date || "∞"}</div>
                <div className="col-span-1 flex items-center gap-1 flex-wrap">
                  <Badge variant="outline" className={`rounded-none text-[10px] ${PKG_STATUS_TONES[p.status] || ""}`}>{p.status}</Badge>
                  {p.status === "active" && pkgHealth[p.id] && pkgHealth[p.id].expected > 0 && (
                    pkgHealth[p.id].missing === 0 ? (
                      <span
                        title={`On-track · ${pkgHealth[p.id].existing}/${pkgHealth[p.id].expected} deliverables generated for ${healthMonth}`}
                        className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-1 py-[1px] rounded-none whitespace-nowrap"
                        data-testid={`pkg-row-health-ok-${p.id}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        On-track
                      </span>
                    ) : (
                      <span
                        title={`${pkgHealth[p.id].missing} of ${pkgHealth[p.id].expected} deliverables missing on Content Calendar for ${healthMonth}. Open the calendar → "Sync missing now" to generate them.`}
                        className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold text-amber-800 bg-amber-50 border border-amber-400 px-1 py-[1px] rounded-none whitespace-nowrap"
                        data-testid={`pkg-row-health-gap-${p.id}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Gap: {pkgHealth[p.id].missing}
                      </span>
                    )
                  )}
                </div>
                {canPrice ? (
                  <div className="col-span-1 text-right font-mono">{fmtMoney(p.totals?.net_monthly_price, p.currency)}</div>
                ) : (
                  <div className="col-span-1 text-right font-mono">{p.totals?.monthly_hours ?? "—"}h</div>
                )}
                {canMargin ? (
                  <div className={`col-span-1 text-right font-mono ${p.totals?.margin_pct < 0 ? "text-red-600" : "text-emerald-700"}`}>{p.totals?.margin_pct}%</div>
                ) : (
                  <div className="col-span-1 text-right">{p.lines_count}</div>
                )}
              </Link>
            ))}
          </div>
          );
        })()}
      {creating && <NewPackageDialog wsId={wsId} brands={brands} onClose={() => setCreating(false)} onCreated={load} />}
      {/* R160e · Project tasks / status quick-view popups triggered from the Project column. */}
      {tasksProject && (
        <TaskPeekDialog
          open
          onOpenChange={(o) => { if (!o) setTasksProject(null); }}
          wsId={wsId}
          projectId={tasksProject.id}
          projectName={tasksProject.name}
        />
      )}
      {statusProject && (
        <ProjectUpdatesPeekDialog
          open
          onOpenChange={(o) => { if (!o) setStatusProject(null); }}
          projectId={statusProject.id}
          projectName={statusProject.name}
        />
      )}
    </div>
  );
}

export function NewPackageDialog({ wsId, brands, defaultBrandId, onClose, onCreated }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", brand_id: defaultBrandId || "", start_date: new Date().toISOString().slice(0, 10), end_date: "", template_id: "" });
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  useEffect(() => { api.get(`/workspaces/${wsId}/package-templates`).then((r) => setTemplates(r.data || [])).catch(() => {}); }, [wsId]);
  const save = async () => {
    if (!form.name.trim() && !form.template_id) { toast.error("Package name required"); return; }
    if (!form.brand_id) { toast.error("Pick a brand"); return; }
    setBusy(true);
    try {
      let r;
      if (form.template_id) {
        r = await api.post(`/workspaces/${wsId}/package-templates/${form.template_id}/apply`,
          { brand_id: form.brand_id, name: form.name || null, start_date: form.start_date, end_date: form.end_date || null });
      } else {
        r = await api.post(`/workspaces/${wsId}/packages`,
          { name: form.name, brand_id: form.brand_id, start_date: form.start_date, end_date: form.end_date || null, lines: [] });
      }
      toast.success("Package created (draft)");
      onCreated?.();
      onClose();
      nav(`/app/packages/${r.data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="new-package-dialog">
        <DialogHeader><DialogTitle>New package</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {templates.length > 0 && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Start from template (optional)</Label>
              <Select value={form.template_id || "_none"} onValueChange={(v) => set("template_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="pkg-template-select"><SelectValue placeholder="Blank package" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">Blank package</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({(t.lines || []).length} lines)</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Package name {form.template_id ? "(blank = template name)" : "*"}</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Social Growth — Monthly" className="h-10 rounded-none" data-testid="pkg-name-input" />
          </div>
          {!defaultBrandId && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Brand *</Label>
              <Select value={form.brand_id || "_none"} onValueChange={(v) => set("brand_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="pkg-brand-select"><SelectValue placeholder="Pick brand" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">Pick brand…</SelectItem>{(brands || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1 inline-block">Start date *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="h-10 rounded-none" data-testid="pkg-start-input" /></div>
            <div><Label className="label-eyebrow mb-1 inline-block">End date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="h-10 rounded-none" data-testid="pkg-end-input" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="pkg-create-save">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Templates tab ───────────────
// Iter Feb 2026 R35 · Dedicated tab listing every saved package template
// (previously buried behind a "Start from template" dropdown inside the
// "New package" dialog — customers couldn't find it). Actions per row:
// • Create package from this template (opens a small brand+date picker).
// • Delete template.
function TemplatesTab({ wsId }) {
  const [templates, setTemplates] = useState(null);
  const [brands, setBrands] = useState([]);
  const [applying, setApplying] = useState(null);   // {template, ...}
  const [propagating, setPropagating] = useState(null); // template being propagated
  const [editing, setEditing] = useState(null);     // template being edited

  const load = () => api.get(`/workspaces/${wsId}/package-templates`)
    .then((r) => setTemplates(r.data || []))
    .catch(() => setTemplates([]));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/brands`).then((r) => setBrands(r.data || [])).catch(() => {}); }, [wsId]);

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Packages already created from it are unaffected.`)) return;
    try {
      await api.delete(`/package-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // Iter · Feb 2026 — Fork a template so users can edit the copy
  // without touching the original.
  const duplicate = async (t) => {
    try {
      const r = await api.post(`/package-templates/${t.id}/duplicate`, {});
      toast.success(`Duplicated as "${r.data.name}"`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Duplicate failed")); }
  };

  // Iter · Feb 2026 — One-click repair for the pre-R98 section-grouping
  // bug. Fires the backfill endpoint that walks every template in the
  // workspace, restores its section grouping from source_package_id,
  // and propagates the fix to every child package with template_id.
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (!window.confirm("Repair package templates and their child packages that were saved before the section-grouping fix? This is safe to run more than once.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/package-templates/backfill-groups`);
      const fixed = (data.results || []).reduce((n, r) => n + (r.children_fixed || 0), 0);
      toast.success(`Repair complete · ${data.total_templates} templates checked · ${fixed} packages fixed`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Repair failed")); }
    finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-3" data-testid="templates-tab">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground flex-1 min-w-0">
          Reusable package blueprints. Save a package as a template from any package&apos;s <em>Save as template</em> action, then spin up new packages here in seconds.
        </div>
        <button
          type="button"
          onClick={runBackfill}
          disabled={backfilling}
          className="text-xs px-2.5 h-7 border border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-800 inline-flex items-center gap-1 shrink-0 disabled:opacity-50 rounded-none"
          title="Fix packages that lost section grouping when created from a pre-R98 template"
          data-testid="packages-templates-backfill"
        >
          {backfilling ? "Repairing…" : "Repair grouping on old packages"}
        </button>
      </div>
      {!templates ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : templates.length === 0 ? (
        <EmptyState
          title="No saved templates yet"
          description="Open any package and click 'Save as template' to reuse it across brands."
          icon={FileStack}
          testid="templates-empty"
        />
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="templates-list">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            <div className="col-span-5">Template</div>
            <div className="col-span-2">Lines</div>
            <div className="col-span-2">Currency</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {templates.map((t) => (
            <div
              key={t.id}
              className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm hover:bg-muted/30"
              data-testid={`template-row-${t.id}`}
            >
              <div className="col-span-5 min-w-0">
                <div className="font-semibold truncate">{t.name}</div>
                {(t.updated_by_name || t.updated_at) && (
                  <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 truncate" data-testid={`template-updated-${t.id}`}>
                    <History className="w-3 h-3" />
                    {t.updated_by_name
                      ? <>Edited by <b className="font-medium">{t.updated_by_name}</b>{t.updated_at ? ` · ${new Date(t.updated_at).toLocaleDateString()}` : ""}</>
                      : <>Last updated · {new Date(t.updated_at).toLocaleDateString()}</>}
                  </div>
                )}
              </div>
              <div className="col-span-2 text-muted-foreground">{(t.lines || []).length}</div>
              <div className="col-span-2 text-xs font-mono">{t.currency || "INR"}</div>
              <div className="col-span-2 text-xs text-muted-foreground">
                {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <Button
                  size="sm"
                  className="rounded-none h-7 text-[10px]"
                  onClick={() => setApplying(t)}
                  data-testid={`template-use-${t.id}`}
                >
                  <Plus className="w-3 h-3 mr-1" /> Use
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none h-7 text-[10px]"
                  onClick={() => setEditing(t)}
                  data-testid={`template-edit-${t.id}`}
                  title="Edit template — rename, re-group lines, change categories"
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none h-7 text-[10px]"
                  onClick={() => duplicate(t)}
                  data-testid={`template-duplicate-${t.id}`}
                  title="Fork this template so you can edit the copy without touching the original"
                >
                  <Copy className="w-3 h-3 mr-1" /> Duplicate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none h-7 text-[10px]"
                  onClick={() => setPropagating(t)}
                  data-testid={`template-propagate-${t.id}`}
                  title="Push template changes onto packages that inherited from it"
                >
                  Propagate
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-none h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => del(t)}
                  data-testid={`template-delete-${t.id}`}
                  title="Delete template"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {applying && (
        <ApplyTemplateDialog
          wsId={wsId}
          template={applying}
          brands={brands}
          onClose={() => setApplying(null)}
          onApplied={() => { setApplying(null); load(); }}
        />
      )}
      {propagating && (
        <TemplatePropagationDialog
          template={propagating}
          onClose={() => setPropagating(null)}
          onApplied={() => { setPropagating(null); load(); }}
        />
      )}
      {editing && (
        <EditTemplateDialog
          template={editing}
          wsId={wsId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ApplyTemplateDialog({ wsId, template, brands, onClose, onApplied }) {
  const nav = useNavigate();
  const [form, setForm] = useState({
    brand_id: "",
    name: "",   // blank = use template.name
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!form.brand_id) { toast.error("Pick a brand"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/package-templates/${template.id}/apply`,
        {
          brand_id: form.brand_id,
          name: form.name || null,
          start_date: form.start_date,
          end_date: form.end_date || null,
        },
      );
      toast.success(`Package created from "${template.name}"`);
      onApplied?.();
      nav(`/app/packages/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to create package from template")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="apply-template-dialog">
        <DialogHeader>
          <DialogTitle>Create package from template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground border border-border px-3 py-2 bg-muted/30">
            <span className="font-semibold text-foreground">{template.name}</span>
            {" · "}
            {(template.lines || []).length} lines
            {template.currency ? ` · ${template.currency}` : ""}
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Brand *</Label>
            <Select value={form.brand_id || "_none"} onValueChange={(v) => set("brand_id", v === "_none" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-none" data-testid="apply-template-brand"><SelectValue placeholder="Pick brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Pick brand…</SelectItem>
                {(brands || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Package name (blank = &quot;{template.name}&quot;)</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={template.name} className="h-10 rounded-none" data-testid="apply-template-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Start date *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="h-10 rounded-none" data-testid="apply-template-start" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">End date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="h-10 rounded-none" data-testid="apply-template-end" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none" data-testid="apply-template-submit">
            {busy ? "Creating…" : "Create package"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Activity master ───────────────

function ActivityMasterTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("acq-activity-master-mode") || "grid"; }
    catch { return "grid"; }
  });
  const setModeAndStore = (m) => {
    setMode(m);
    try { localStorage.setItem("acq-activity-master-mode", m); } catch { /* ignore */ }
  };
  const load = () => api.get(`/workspaces/${wsId}/activity-types?include_inactive=false`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  const del = async (a) => {
    if (!window.confirm(`Deactivate "${a.name}"?`)) return;
    await api.delete(`/activity-types/${a.id}`);
    toast.success("Deactivated");
    load();
  };

  if (mode === "grid") {
    return (
      <div className="space-y-3" data-testid="activity-master-tab">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">
            Excel-style grid — inline edit any cell. Skill columns pull from breakdowns you&apos;ve defined; rate/hour header cells are the workspace rate card.
          </span>
          <div className="flex items-center gap-2">
            <div className="inline-flex border border-border" data-testid="activity-master-mode-toggle">
              <button
                type="button"
                onClick={() => setModeAndStore("grid")}
                className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${mode === "grid" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                data-testid="activity-master-mode-grid"
              >Grid</button>
              <button
                type="button"
                onClick={() => setModeAndStore("card")}
                className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${mode === "card" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                data-testid="activity-master-mode-card"
              >Card list</button>
            </div>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={async () => {
                if (!window.confirm("Seed 40+ Media & Marketing activity types (Reels, Carousels, Blog Posts, Google Ads, etc.)? Existing activities with the same name will be skipped.")) return;
                try {
                  const { data } = await api.post(`/workspaces/${wsId}/activity-types/seed-media-marketing`);
                  toast.success(`Seed complete · ${data.inserted} added, ${data.skipped} skipped (already existed)`);
                  load();
                } catch (e) {
                  toast.error(apiErrorMessage(e, "Seed failed"));
                }
              }}
              data-testid="activity-seed-media-marketing"
              title="Insert 40+ curated Media & Marketing activity types"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Seed preset
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={async () => {
                if (!window.confirm("Sprinkle assignees onto tasks that currently have none? Distributes round-robin across workspace members.")) return;
                try {
                  const r = await api.post(`/workspaces/${wsId}/tasks/backfill-assignees`, { strategy: "round_robin", only_missing: true, limit: 5000 });
                  toast.success(`Assigned ${r.data.updated} tasks across ${r.data.member_count} members`);
                } catch (e) { toast.error(apiErrorMessage(e, "Backfill failed")); }
              }}
              data-testid="activity-backfill-assignees"
              title="Round-robin an assignee onto every task that currently has none"
            >
              <Users className="w-4 h-4 mr-2" /> Assign seed tasks
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setManageCatsOpen(true)}
              data-testid="activity-manage-cats-btn"
              title="Add / rename / recolor content-calendar categories"
            >
              <Tag className="w-4 h-4 mr-2" /> Manage categories
            </Button>
          </div>
        </div>
        <ActivityMasterGrid
          wsId={wsId}
          onOpenAdvanced={(a) => setEditing(a)}
        />
        {editing && <ActivityDialog wsId={wsId} activity={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        <ManageCategoriesDialog wsId={wsId} open={manageCatsOpen} onClose={() => setManageCatsOpen(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="activity-master-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Master list of everything you deliver — deliverables, meetings, docs, website updates, ad-hoc work. Used to build packages fast.</span>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-border" data-testid="activity-master-mode-toggle">
            <button
              type="button"
              onClick={() => setModeAndStore("grid")}
              className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${mode === "grid" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="activity-master-mode-grid"
            >Grid</button>
            <button
              type="button"
              onClick={() => setModeAndStore("card")}
              className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${mode === "card" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="activity-master-mode-card"
            >Card list</button>
          </div>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={async () => {
              if (!window.confirm("Seed 40+ Media & Marketing activity types (Reels, Carousels, Blog Posts, Google Ads, etc.)? Existing activities with the same name will be skipped.")) return;
              try {
                const { data } = await api.post(`/workspaces/${wsId}/activity-types/seed-media-marketing`);
                toast.success(`Seed complete · ${data.inserted} added, ${data.skipped} skipped (already existed)`);
                load();
              } catch (e) {
                toast.error(apiErrorMessage(e, "Seed failed"));
              }
            }}
            data-testid="activity-seed-media-marketing"
            title="Insert 40+ curated Media & Marketing activity types"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Seed Media &amp; Marketing preset
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={async () => {
              if (!window.confirm("Sprinkle assignees onto tasks that currently have none? Distributes round-robin across workspace members.")) return;
              try {
                const r = await api.post(`/workspaces/${wsId}/tasks/backfill-assignees`, { strategy: "round_robin", only_missing: true, limit: 5000 });
                toast.success(`Assigned ${r.data.updated} tasks across ${r.data.member_count} members`);
              } catch (e) { toast.error(apiErrorMessage(e, "Backfill failed")); }
            }}
            data-testid="activity-backfill-assignees"
            title="Round-robin an assignee onto every task that currently has none"
          >
            <Users className="w-4 h-4 mr-2" /> Assign seed tasks
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setManageCatsOpen(true)}
            data-testid="activity-manage-cats-btn"
            title="Add / rename / recolor content-calendar categories"
          >
            <Tag className="w-4 h-4 mr-2" /> Manage categories
          </Button>
          <Button className="rounded-none" onClick={() => setEditing({})} data-testid="activity-new-btn"><Plus className="w-4 h-4 mr-2" /> New activity</Button>
        </div>
      </div>
      {!rows ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No activities defined" description="Define your first activity type — e.g. Reel, Static Post, Blog, Website Update, Client Meeting." icon={ListChecks} testid="activity-empty" action={<Button className="rounded-none" onClick={() => setEditing({})} data-testid="activity-empty-cta">New activity</Button>} />
        : (() => {
          const showRate = rows.some((a) => "default_rate" in a);
          const showCost = rows.some((a) => "default_cost" in a);
          return (
          <div className="border border-border divide-y divide-border" data-testid="activity-list">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <div className="col-span-3">Activity</div><div className="col-span-2">Category</div><div className="col-span-1">Scope</div>
              <div className={showRate || showCost ? "col-span-2 text-right" : "col-span-5 text-right"}>Effort / unit</div>
              {showRate && <div className="col-span-1 text-right">Rate</div>}
              {showCost && <div className="col-span-2 text-right">Cost / unit</div>}
              <div className="col-span-1"></div>
            </div>
            {rows.map((a) => (
              <div key={a.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm hover:bg-muted/30" data-testid={`activity-row-${a.id}`}>
                <button className="col-span-3 font-semibold truncate text-left hover:text-primary flex items-center gap-1.5"
                          onClick={() => setEditing(a)} data-testid={`activity-edit-${a.id}`}>
                  {(a.color || a.icon_key) && (
                    <TypeIcon iconKey={a.icon_key} color={a.color} size={14} />
                  )}
                  <span className="truncate">{a.name}</span>
                </button>
                <div className="col-span-2"><Badge variant="outline" className="rounded-none text-[10px]">{a.category}</Badge></div>
                <div className="col-span-1 text-xs">{a.is_external ? "external" : "internal"}</div>
                <div className={`text-right font-mono text-xs ${showRate || showCost ? "col-span-2" : "col-span-5"}`}>{a.default_effort_hours}h ({(a.skill_breakdown || []).length} skills)</div>
                {showRate && <div className="col-span-1 text-right font-mono text-xs">{fmtMoney(a.default_rate)}</div>}
                {showCost && <div className="col-span-2 text-right font-mono text-xs">{fmtMoney(a.default_cost)}</div>}
                <div className="col-span-1 text-right"><button onClick={() => del(a)} className="text-destructive" data-testid={`activity-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button></div>
              </div>
            ))}
          </div>
          );
        })()}
      {editing && <ActivityDialog wsId={wsId} activity={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ManageCategoriesDialog wsId={wsId} open={manageCatsOpen} onClose={() => setManageCatsOpen(false)} />
    </div>
  );
}

function ActivityDialog({ wsId, activity, onClose, onSaved }) {
  const isNew = !activity?.id;
  const [form, setForm] = useState({
    name: activity.name || "", category: activity.category || "other",
    sop_id: activity.sop_id || "", is_external: activity.is_external !== false,
    default_effort_hours: activity.default_effort_hours ?? 0,
    skill_breakdown: activity.skill_breakdown || [], default_rate: activity.default_rate ?? 0,
    description: activity.description || "",
    qc_checklist: activity.qc_checklist || [],
    // Iter 187 — visual identity
    color: activity.color || "",
    icon_key: activity.icon_key || "",
  });
  const [qcDraft, setQcDraft] = useState("");
  const [sops, setSops] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [iconRegistry, setIconRegistry] = useState([]);
  const [categories, setCategories] = useState([]); // Iter · Feb 2026 — workspace list
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    api.get(`/workspaces/${wsId}/sops`).then((r) => setSops(r.data || [])).catch(() => {});
    api.get(`/content-bucket/icon-registry`).then((r) => setIconRegistry(r.data?.icons || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/content-categories`).then((r) => setCategories(r.data || [])).catch(() => {});
    Promise.all([
      api.get(`/workspaces/${wsId}/designations`),
      api.get(`/workspaces/${wsId}/designation-rates`),
    ]).then(([d, r]) => setDesignations(Array.from(new Set([...(d.data || []), ...Object.keys(r.data?.rates || {})])).sort())).catch(() => {});
  }, [wsId]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form, sop_id: form.sop_id || null,
        default_effort_hours: Number(form.default_effort_hours) || 0,
        default_rate: Number(form.default_rate) || 0,
        skill_breakdown: (form.skill_breakdown || []).filter((s) => s.designation).map((s) => ({ designation: s.designation, hours: Number(s.hours) || 0 })),
        color: (form.color || "").trim() || null,
        icon_key: (form.icon_key || "").trim() || null,
      };
      if (isNew) await api.post(`/workspaces/${wsId}/activity-types`, payload);
      else await api.patch(`/activity-types/${activity.id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="activity-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New activity type" : `Edit · ${activity.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1 inline-block">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Reel" className="h-10 rounded-none" data-testid="act-name" /></div>
            <div><Label className="label-eyebrow mb-1 inline-block">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="act-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories.length ? categories : CATEGORIES.map((c) => ({ name: c }))).map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      <span className="inline-flex items-center gap-1.5">
                        {c.color && <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />}
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1 inline-block">Linked SOP</Label>
              <Select value={form.sop_id || "_none"} onValueChange={(v) => set("sop_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="act-sop"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">None</SelectItem>{sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-eyebrow mb-1 inline-block">Scope</Label>
              <Select value={form.is_external ? "external" : "internal"} onValueChange={(v) => set("is_external", v === "external")}>
                <SelectTrigger className="h-10 rounded-none" data-testid="act-scope"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="external">External (client deliverable)</SelectItem><SelectItem value="internal">Internal (ops work)</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow mb-1 inline-block">Est. effort / unit (hours)</Label>
              <Input type="number" min={0} step={0.25} value={form.default_effort_hours} onChange={(e) => set("default_effort_hours", e.target.value)} className="h-10 rounded-none" data-testid="act-effort" /></div>
            <div><Label className="label-eyebrow mb-1 inline-block">Default rate / unit</Label>
              <Input type="number" min={0} value={form.default_rate} onChange={(e) => set("default_rate", e.target.value)} className="h-10 rounded-none" data-testid="act-rate" /></div>
          </div>
          <div>
            <RowsEditor label="Skill breakdown (per unit)" testid="act-skills"
              columns={[
                { key: "designation", label: "Designation", type: "select", options: designations },
                { key: "hours", label: "Hours", type: "number" },
              ]}
              rows={form.skill_breakdown} onChange={(v) => set("skill_breakdown", v)} addLabel="Add skill" />
            {designations.length === 0 && <p className="text-[10px] text-muted-foreground mt-1">No designations found in HRMS employees — add designations to employees first.</p>}
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Visual identity (Iter 187)</Label>
            <div className="border border-border p-2 space-y-2 bg-muted/20">
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.color || "#7c3aed"}
                             onChange={(e) => set("color", e.target.value)}
                             className="h-8 w-14 border border-border rounded-none cursor-pointer"
                             data-testid="act-color-picker" />
                    <Input value={form.color || ""}
                             onChange={(e) => set("color", e.target.value)}
                             placeholder="#e1306c"
                             className="h-8 rounded-none text-xs font-mono"
                             data-testid="act-color-hex" />
                    {form.color && (
                      <button type="button" onClick={() => set("color", "")}
                                className="text-[10px] text-muted-foreground hover:text-rose-600"
                                title="Clear color">✕</button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Icon</Label>
                  <Select value={form.icon_key || "_none"}
                            onValueChange={(v) => set("icon_key", v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-8 rounded-none text-xs" data-testid="act-icon-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— No icon —</SelectItem>
                      {iconRegistry.map((ic) => (
                        <SelectItem key={ic.key} value={ic.key}>
                          {ic.label} <span className="text-muted-foreground ml-1 font-mono">{ic.key}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(form.color || form.icon_key) && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Preview:</span>
                  <span className="inline-flex items-center gap-1 border px-2 py-0.5 font-medium"
                          style={{
                            borderColor: (form.color || "#7c3aed") + "66",
                            background: (form.color || "#7c3aed") + "12",
                            color: form.color || "#7c3aed",
                          }}
                          data-testid="act-visual-preview">
                    <TypeIcon iconKey={form.icon_key} color={form.color} size={13} />
                    {form.name || "Activity"}
                  </span>
                </div>
              )}
              <div className="text-[10px] text-muted-foreground">Used across Content Calendar &amp; Content Bucket to color-code deliverables of this type everywhere.</div>
            </div>
          </div>
          <div><Label className="label-eyebrow mb-1 inline-block">Description / notes</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="h-10 rounded-none" data-testid="act-description" /></div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">QC checklist</Label>
            <div className="space-y-1 mb-1.5">
              {(form.qc_checklist || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-muted/30 px-2 py-1">
                  <span className="flex-1 truncate">{c}</span>
                  <button onClick={() => set("qc_checklist", form.qc_checklist.filter((_, j) => j !== i))} className="text-destructive" data-testid={`act-qc-del-${i}`}>✕</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={qcDraft} onChange={(e) => setQcDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (qcDraft.trim()) { set("qc_checklist", [...(form.qc_checklist || []), qcDraft.trim()]); setQcDraft(""); } } }}
                placeholder="e.g. Caption approved, brand colors used…" className="h-8 rounded-none text-xs" data-testid="act-qc-input" />
              <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => { if (qcDraft.trim()) { set("qc_checklist", [...(form.qc_checklist || []), qcDraft.trim()]); setQcDraft(""); } }} data-testid="act-qc-add"><Plus className="w-3 h-3" /></Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="act-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Rate card ───────────────

function RateCardTab({ wsId }) {
  const [rates, setRates] = useState({});
  const [designations, setDesignations] = useState([]);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    Promise.all([
      api.get(`/workspaces/${wsId}/designation-rates`),
      api.get(`/workspaces/${wsId}/designations`),
    ]).then(([r1, r2]) => {
      setRates(r1.data?.rates || {});
      setDesignations(r2.data || []);
    }).catch(() => {});
  }, [wsId]);

  const allKeys = Array.from(new Set([...designations, ...Object.keys(rates)])).sort();

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/designation-rates`, { rates });
      toast.success("Rate card saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl space-y-3" data-testid="rate-card-tab">
      <div className="max-w-2xl">
      <p className="text-sm text-muted-foreground">Internal hourly cost per designation — drives package cost & margin computations. Designations come from your HRMS employees; you can also add custom rows.</p>
      <div className="border border-border divide-y divide-border">
        <div className="grid grid-cols-2 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          <div>Designation</div><div className="text-right">Hourly cost (₹)</div>
        </div>
        {allKeys.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">No designations yet — add one below.</div>}
        {allKeys.map((d) => (
          <div key={d} className="grid grid-cols-2 gap-2 px-3 py-2 items-center" data-testid={`rate-row-${d}`}>
            <div className="text-sm font-medium">{d}</div>
            <div className="flex justify-end">
              <Input type="number" min={0} value={rates[d] ?? ""} onChange={(e) => setRates((s) => ({ ...s, [d]: e.target.value }))} className="h-8 w-32 rounded-none text-right font-mono" data-testid={`rate-input-${d}`} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Add custom designation…" className="h-9 rounded-none" data-testid="rate-custom-input" />
        <Button variant="outline" className="rounded-none h-9" onClick={() => { const v = custom.trim(); if (v) { setRates((s) => ({ ...s, [v]: s[v] ?? 0 })); setCustom(""); } }} data-testid="rate-custom-add"><Plus className="w-3.5 h-3.5" /></Button>
        <Button className="rounded-none h-9 ml-auto" onClick={save} disabled={busy} data-testid="rate-save"><Save className="w-3.5 h-3.5 mr-1.5" />{busy ? "Saving…" : "Save rate card"}</Button>
      </div>
      </div>
      <div className="pt-4 mt-6 border-t">
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Versioned rate cards</div>
          <p className="text-xs text-muted-foreground mt-1">Create named versions with validity dates and brand-specific overrides for accurate historical calculations.</p>
        </div>
        <RateCardsManager wsId={wsId} />
      </div>
    </div>
  );
}


// ─────────────── Package Planner (All packages dashboard) ───────────────

function PackagePlannerTab({ wsId }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [pivot, setPivot] = useState(true);
  const [status, setStatus] = useState("_all");

  useEffect(() => {
    if (!wsId) return;
    const p = new URLSearchParams({ include_lines: "true" });
    if (status !== "_all") p.set("status", status);
    api.get(`/workspaces/${wsId}/packages?${p.toString()}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [wsId, status]);

  if (rows == null) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (rows.length === 0) return <EmptyState title="No packages yet" description="Create packages to see them side-by-side here." />;

  // Build the union of item names + counts per package
  const itemSet = new Set();
  rows.forEach((p) => (p.lines || []).forEach((l) => itemSet.add((l.name || "").trim() || "—")));
  const items = Array.from(itemSet).sort();

  // Long-format rows (one entry per package-line)
  const longRows = [];
  rows.forEach((p) => (p.lines || []).forEach((l) => {
    longRows.push({
      pkg_id: p.id, pkg_name: p.name, brand: p.brand_name || "—",
      status: p.status, currency: p.currency,
      item: (l.name || "").trim() || "—",
      count: Number(l.count) || 0,
      frequency: FREQ_LABELS[l.frequency] || l.frequency || "—",
      monthly_qty: l.monthly_qty ?? "—",
      monthly_price: l.monthly_price ?? null,
    });
  }));

  const copyPivot = async () => {
    const header = ["Item", ...rows.map((p) => `${p.name} (${p.brand_name || ""})`)];
    const body = items.map((it) => {
      const counts = rows.map((p) => {
        const l = (p.lines || []).find((x) => ((x.name || "").trim() || "—") === it);
        if (!l) return "";
        return `${Number(l.count) || 0} × ${FREQ_LABELS[l.frequency] || l.frequency || ""}`;
      });
      return [it, ...counts];
    });
    const tsv = [header.join("\t"), ...body.map((r) => r.join("\t"))].join("\n");
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success("Pivot table copied — paste into Sheets or Docs");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const copyLong = async () => {
    const header = ["Package", "Brand", "Status", "Item", "Count", "Frequency", "Monthly qty"];
    const body = longRows.map((r) => [r.pkg_name, r.brand, r.status, r.item, r.count, r.frequency, r.monthly_qty]);
    const tsv = [header.join("\t"), ...body.map((r) => r.join("\t"))].join("\n");
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success("Long table copied — paste into Sheets");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="pkg-planner-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="label-eyebrow text-[10px] mr-2">View</Label>
        <div className="inline-flex border border-border" data-testid="pkg-planner-view-toggle">
          <button type="button" onClick={() => setPivot(true)} className={`px-3 py-1.5 text-xs ${pivot ? "bg-primary text-primary-foreground" : "bg-transparent"}`} data-testid="pkg-planner-pivot-btn">Pivot (items × packages)</button>
          <button type="button" onClick={() => setPivot(false)} className={`px-3 py-1.5 text-xs border-l border-border ${!pivot ? "bg-primary text-primary-foreground" : "bg-transparent"}`} data-testid="pkg-planner-long-btn">Long (one row per line)</button>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="pkg-planner-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All statuses</SelectItem>
            {Object.keys(PKG_STATUS_TONES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground ml-2">{rows.length} packages · {items.length} unique items · {longRows.length} lines</span>
        <Button size="sm" variant="outline" className="rounded-none h-9 ml-auto" onClick={pivot ? copyPivot : copyLong} data-testid="pkg-planner-copy">
          <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy table
        </Button>
      </div>

      {pivot ? (
        <div className="border border-border overflow-x-auto" data-testid="pkg-planner-pivot">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 border-r border-border sticky left-0 bg-muted/40 z-10">Item</th>
                {rows.map((p) => (
                  <th key={p.id} className="text-left px-2 py-2 border-r border-border last:border-0 min-w-[140px]">
                    <button className="text-primary hover:underline text-left" onClick={() => nav(`/app/packages/${p.id}`)}>{p.name}</button>
                    <div className="text-[10px] text-muted-foreground font-normal">{p.brand_name || "—"} · <Badge variant="outline" className={`rounded-none text-[9px] ${PKG_STATUS_TONES[p.status] || ""}`}>{p.status}</Badge></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it} className="border-t border-border hover:bg-muted/10">
                  <td className="px-3 py-1.5 border-r border-border sticky left-0 bg-background z-10 font-medium">{it}</td>
                  {rows.map((p) => {
                    const l = (p.lines || []).find((x) => ((x.name || "").trim() || "—") === it);
                    return (
                      <td key={p.id} className="px-2 py-1.5 border-r border-border last:border-0 font-mono">
                        {l ? (
                          <span>{Number(l.count) || 0}<span className="text-[10px] text-muted-foreground"> × {FREQ_LABELS[l.frequency] || l.frequency}</span></span>
                        ) : <span className="text-muted-foreground/50">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="pkg-planner-long">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 border-r border-border">Package</th>
                <th className="text-left px-3 py-2 border-r border-border">Brand</th>
                <th className="text-left px-3 py-2 border-r border-border">Status</th>
                <th className="text-left px-3 py-2 border-r border-border">Item</th>
                <th className="text-right px-3 py-2 border-r border-border">Count</th>
                <th className="text-left px-3 py-2 border-r border-border">Frequency</th>
                <th className="text-right px-3 py-2">Monthly qty</th>
              </tr>
            </thead>
            <tbody>
              {longRows.map((r, i) => (
                <tr key={i} className="border-t border-border hover:bg-muted/10">
                  <td className="px-3 py-1.5 border-r border-border"><button className="text-primary hover:underline" onClick={() => nav(`/app/packages/${r.pkg_id}`)}>{r.pkg_name}</button></td>
                  <td className="px-3 py-1.5 border-r border-border">{r.brand}</td>
                  <td className="px-3 py-1.5 border-r border-border"><Badge variant="outline" className={`rounded-none text-[10px] ${PKG_STATUS_TONES[r.status] || ""}`}>{r.status}</Badge></td>
                  <td className="px-3 py-1.5 border-r border-border">{r.item}</td>
                  <td className="px-3 py-1.5 border-r border-border text-right font-mono">{r.count}</td>
                  <td className="px-3 py-1.5 border-r border-border">{r.frequency}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{r.monthly_qty}</td>
                </tr>
              ))}
              {longRows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-[11px]">No line items across selected packages.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
